import React from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  Box,
  Button,
  HStack,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { MdFileDownload } from 'react-icons/md';
import { XactoMatrixData, XactoGroupBy } from '../../hooks/useXactoMatrix';
import { AiAnalysisPanel } from './AiAnalysisPanel';

interface XactoPivotProps {
  xactoData: XactoMatrixData | null;
  groupBy?: XactoGroupBy;
  onCellClick?: (group: string | undefined, month: string | undefined, amount: number) => void;
  orientation?: 'vertical' | 'horizontal';
  onExport?: () => void;
  showToolbarActions?: boolean;
}

const MONTH_NAMES = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
const formatMonthLabel = (yyyyMM: string): string => {
  const [year, month] = yyyyMM.split('-');
  const idx = parseInt(month, 10) - 1;
  return idx >= 0 && idx < 12 ? `${MONTH_NAMES[idx]}-${year}` : yyyyMM;
};

const formatSigned = (val: number): string => {
  const abs = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(Math.abs(val));
  return val > 0 ? `+${abs}` : val < 0 ? `-${abs}` : abs;
};

/**
 * XactoPivot — inverted pivot matching the Excel export format:
 *   Rows    = Months (YYYY-MM)
 *   Columns = dynamic categories or merchants
 *   Values  = aggregated amount per month per group
 */
export const XactoPivot: React.FC<XactoPivotProps> = ({
  xactoData,
  groupBy = 'category',
  onCellClick,
  orientation = 'horizontal',
  onExport,
  showToolbarActions = true,
}) => {
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const theadBg = useColorModeValue('gray.50', 'gray.800');
  const tfootBg = useColorModeValue('gray.100', 'gray.900');
  const { isOpen: isPanelOpen, onOpen: openPanel, onClose: closePanel } = useDisclosure();

  if (!xactoData || xactoData.rows.length === 0) return null;

  const { columns, columnLabels, rows, colTotals, checkData, zelleData, categoryGroups } = xactoData;
  const hasChecks = checkData.totals.in > 0 || checkData.totals.out > 0;
  const hasZelle = zelleData.totals.in > 0 || zelleData.totals.out > 0;
  const months = rows.map(r => r.month);
  const groupLabel = groupBy === 'category' ? 'Category' : groupBy === 'subcategory' ? 'Subcategory' : groupBy === 'vendor' ? 'Vendor' : 'Merchant';
  const isCategoryGrouped = groupBy === 'category' && !!categoryGroups;

  // ── Vertical (transposed): rows = groups, columns = months ────────────────
  if (orientation === 'vertical') {
    return (
      <>
      <Box>
        {showToolbarActions && (
          <HStack justify="flex-end" mb={2}>
            <Button size="sm" variant="outline" colorScheme="purple" leftIcon={<span>✨</span>} onClick={openPanel}>
              AI Analysis
            </Button>
            {onExport && (
              <Button
                size="sm"
                variant="outline"
                colorScheme="green"
                leftIcon={<MdFileDownload />}
                onClick={onExport}
              >
                Export (Pivot Format)
              </Button>
            )}
          </HStack>
        )}
        <Box overflowX="auto" borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
          <Table variant="simple" size="sm">
            <Thead position="sticky" top={0} bg={theadBg} zIndex={1}>
              <Tr bg={theadBg}>
                <Th minW="160px">{isCategoryGrouped ? 'Category' : groupLabel}</Th>
                {isCategoryGrouped && <Th minW="160px">Subcategory</Th>}
                {months.map(m => <Th key={m} isNumeric minW="110px">{formatMonthLabel(m)}</Th>)}
                <Th isNumeric minW="110px" fontWeight="bold">Total</Th>
              </Tr>
            </Thead>
            <Tbody>
              {columns.map(col => {
                const rowTotal = colTotals[col] || 0;
                const [colPrimary, colDetailed] = isCategoryGrouped ? col.split('::') : [col, undefined];
                return (
                  <Tr key={col} _hover={{ bg: hoverBg }}>
                    <Td fontWeight="medium" cursor={onCellClick ? 'pointer' : undefined}
                      _hover={onCellClick ? { textDecoration: 'underline' } : undefined}
                      onClick={onCellClick ? () => onCellClick(col, undefined, rowTotal) : undefined}>
                      {isCategoryGrouped ? colPrimary : (columnLabels[col] ?? col)}
                    </Td>
                    {isCategoryGrouped && (
                      <Td cursor={onCellClick ? 'pointer' : undefined}
                        _hover={onCellClick ? { textDecoration: 'underline' } : undefined}
                        onClick={onCellClick ? () => onCellClick(col, undefined, rowTotal) : undefined}>
                        {colDetailed ?? ''}
                      </Td>
                    )}
                    {months.map(m => {
                      const row = rows.find(r => r.month === m);
                      const val = row?.values[col] || 0;
                      return (
                        <Td key={m} isNumeric
                          color={val === 0 ? 'gray.300' : val < 0 ? 'red.500' : 'green.600'}
                          cursor={onCellClick && val !== 0 ? 'pointer' : undefined}
                          _hover={onCellClick && val !== 0 ? { opacity: 0.7 } : undefined}
                          onClick={onCellClick && val !== 0 ? () => onCellClick(col, m, val) : undefined}>
                          {val !== 0 ? formatSigned(val) : '-'}
                        </Td>
                      );
                    })}
                    <Td isNumeric fontWeight="bold"
                      color={rowTotal === 0 ? 'gray.300' : rowTotal < 0 ? 'red.500' : 'green.600'}
                      cursor={onCellClick ? 'pointer' : undefined}
                      _hover={onCellClick ? { opacity: 0.7 } : undefined}
                      onClick={onCellClick ? () => onCellClick(col, undefined, rowTotal) : undefined}>
                      {formatSigned(rowTotal)}
                    </Td>
                  </Tr>
                );
              })}
              {hasChecks && <>
                <Tr _hover={{ bg: hoverBg }} borderTopWidth="2px">
                  <Td fontWeight="semibold" color="green.600">Checks In</Td>
                  {isCategoryGrouped && <Td />}
                  {months.map(m => { const v = checkData.rows[m]?.in || 0; return <Td key={m} isNumeric color={v ? 'green.600' : 'gray.300'}>{v ? formatSigned(v) : '-'}</Td>; })}
                  <Td isNumeric fontWeight="bold" color="green.600">{formatSigned(checkData.totals.in)}</Td>
                </Tr>
                <Tr _hover={{ bg: hoverBg }}>
                  <Td fontWeight="semibold" color="red.500">Checks Out</Td>
                  {isCategoryGrouped && <Td />}
                  {months.map(m => { const v = checkData.rows[m]?.out || 0; return <Td key={m} isNumeric color={v ? 'red.500' : 'gray.300'}>{v ? formatSigned(-v) : '-'}</Td>; })}
                  <Td isNumeric fontWeight="bold" color="red.500">{formatSigned(-checkData.totals.out)}</Td>
                </Tr>
              </>}
              {hasZelle && <>
                <Tr _hover={{ bg: hoverBg }} borderTopWidth="2px">
                  <Td fontWeight="semibold" color="green.600">Zelle In</Td>
                  {isCategoryGrouped && <Td />}
                  {months.map(m => { const v = zelleData.rows[m]?.in || 0; return <Td key={m} isNumeric color={v ? 'green.600' : 'gray.300'}>{v ? formatSigned(v) : '-'}</Td>; })}
                  <Td isNumeric fontWeight="bold" color="green.600">{formatSigned(zelleData.totals.in)}</Td>
                </Tr>
                <Tr _hover={{ bg: hoverBg }}>
                  <Td fontWeight="semibold" color="red.500">Zelle Out</Td>
                  {isCategoryGrouped && <Td />}
                  {months.map(m => { const v = zelleData.rows[m]?.out || 0; return <Td key={m} isNumeric color={v ? 'red.500' : 'gray.300'}>{v ? formatSigned(-v) : '-'}</Td>; })}
                  <Td isNumeric fontWeight="bold" color="red.500">{formatSigned(-zelleData.totals.out)}</Td>
                </Tr>
              </>}
            </Tbody>
            <Tfoot>
              <Tr bg={tfootBg}>
                <Th fontWeight="black">Total</Th>
                {isCategoryGrouped && <Th />}
                {rows.map(row => {
                  const monthTotal = columns.reduce((s, col) => s + (row.values[col] || 0), 0);
                  return (
                    <Th key={row.month} isNumeric fontWeight="black"
                      color={monthTotal < 0 ? 'red.500' : monthTotal > 0 ? 'green.600' : 'inherit'}
                      cursor={onCellClick ? 'pointer' : undefined}
                      onClick={onCellClick ? () => onCellClick(undefined, row.month, monthTotal) : undefined}>
                      {formatSigned(monthTotal)}
                    </Th>
                  );
                })}
                <Th isNumeric fontWeight="black" fontSize="md"
                  color={colTotals.grandTotal < 0 ? 'red.500' : colTotals.grandTotal > 0 ? 'green.600' : 'inherit'}>
                  {formatSigned(colTotals.grandTotal)}
                </Th>
              </Tr>
            </Tfoot>
          </Table>
        </Box>
      </Box>
      <AiAnalysisPanel isOpen={isPanelOpen} onClose={closePanel} matrixData={xactoData} groupBy={groupBy} />
      </>
    );
  }

  // ── Horizontal (default): rows = months, columns = groups ─────────────────
  return (
    <>
    <Box>
      {/* Toolbar */}
      {showToolbarActions && (
        <HStack justify="flex-end" mb={2}>
          <Button
            size="sm"
            variant="outline"
            colorScheme="purple"
            leftIcon={<span>✨</span>}
            onClick={openPanel}
          >
            AI Analysis
          </Button>
          {onExport && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="green"
              leftIcon={<MdFileDownload />}
              onClick={onExport}
            >
              Export (Pivot Format)
            </Button>
          )}
        </HStack>
      )}

    <Box overflowX="auto" borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
      <Table variant="simple" size="sm">
        <Thead position="sticky" top={0} bg={theadBg} zIndex={1}>
          {isCategoryGrouped ? (
            <>
              <Tr bg={theadBg}>
                <Th rowSpan={2} minW="100px">Month</Th>
                {Object.entries(categoryGroups!).map(([primary, subkeys]) => (
                  <Th key={primary} isNumeric colSpan={subkeys.length} minW="120px"
                    borderLeftWidth="2px" borderLeftColor={borderColor} textAlign="center">
                    {primary}
                  </Th>
                ))}
                {hasChecks && (
                  <>
                    <Th rowSpan={2} isNumeric minW="120px" color="green.600">Checks In</Th>
                    <Th rowSpan={2} isNumeric minW="120px" color="red.500">Checks Out</Th>
                  </>
                )}
                {hasZelle && (
                  <>
                    <Th rowSpan={2} isNumeric minW="120px" color="green.600">Zelle In</Th>
                    <Th rowSpan={2} isNumeric minW="120px" color="red.500">Zelle Out</Th>
                  </>
                )}
                <Th rowSpan={2} isNumeric minW="110px" fontWeight="bold">Total</Th>
              </Tr>
              <Tr bg={theadBg}>
                {columns.map((col, i) => {
                  const isFirstInGroup = Object.values(categoryGroups!).some(subs => subs[0] === col);
                  return (
                    <Th key={col} isNumeric minW="120px"
                      borderLeftWidth={isFirstInGroup ? '2px' : undefined}
                      borderLeftColor={isFirstInGroup ? borderColor : undefined}>
                      {columnLabels[col] ?? col}
                    </Th>
                  );
                })}
              </Tr>
            </>
          ) : (
            <Tr bg={theadBg}>
              <Th minW="100px">Month</Th>
              {columns.map(col => (
                <Th key={col} isNumeric minW="120px">
                  {columnLabels[col] ?? col}
                </Th>
              ))}
              {hasChecks && (
                <>
                  <Th isNumeric minW="120px" color="green.600">Checks In</Th>
                  <Th isNumeric minW="120px" color="red.500">Checks Out</Th>
                </>
              )}
              {hasZelle && (
                <>
                  <Th isNumeric minW="120px" color="green.600">Zelle In</Th>
                  <Th isNumeric minW="120px" color="red.500">Zelle Out</Th>
                </>
              )}
              <Th isNumeric minW="110px" fontWeight="bold">
                Total
              </Th>
            </Tr>
          )}
        </Thead>

        <Tbody>
          {rows.map(row => (
            <Tr key={row.month} _hover={{ bg: hoverBg }}>
              <Td
                fontWeight="medium"
                cursor={onCellClick ? 'pointer' : undefined}
                _hover={onCellClick ? { textDecoration: 'underline' } : undefined}
                onClick={onCellClick ? () => onCellClick(undefined, row.month, row.rowTotal) : undefined}
              >
                {formatMonthLabel(row.month)}
              </Td>
              {columns.map(col => {
                const val = row.values[col] || 0;
                const isFirstInGroup = isCategoryGrouped &&
                  Object.values(categoryGroups!).some(subs => subs[0] === col);
                return (
                  <Td
                    key={col}
                    isNumeric
                    color={val === 0 ? 'gray.300' : val < 0 ? 'red.500' : 'green.600'}
                    cursor={onCellClick && val !== 0 ? 'pointer' : undefined}
                    _hover={onCellClick && val !== 0 ? { opacity: 0.7 } : undefined}
                    onClick={onCellClick && val !== 0 ? () => onCellClick(col, row.month, val) : undefined}
                    borderLeftWidth={isFirstInGroup ? '2px' : undefined}
                    borderLeftColor={isFirstInGroup ? borderColor : undefined}
                  >
                    {val !== 0 ? formatSigned(val) : '-'}
                  </Td>
                );
              })}
              {hasChecks && (() => {
                const chk = checkData.rows[row.month];
                return (
                  <>
                    <Td isNumeric color={chk?.in ? 'green.600' : 'gray.300'}>
                      {chk?.in ? formatSigned(chk.in) : '-'}
                    </Td>
                    <Td isNumeric color={chk?.out ? 'red.500' : 'gray.300'}>
                      {chk?.out ? formatSigned(-chk.out) : '-'}
                    </Td>
                  </>
                );
              })()}
              {hasZelle && (() => {
                const zll = zelleData.rows[row.month];
                return (
                  <>
                    <Td isNumeric color={zll?.in ? 'green.600' : 'gray.300'}>
                      {zll?.in ? formatSigned(zll.in) : '-'}
                    </Td>
                    <Td isNumeric color={zll?.out ? 'red.500' : 'gray.300'}>
                      {zll?.out ? formatSigned(-zll.out) : '-'}
                    </Td>
                  </>
                );
              })()}
              <Td
                isNumeric
                fontWeight="bold"
                cursor={onCellClick ? 'pointer' : undefined}
                _hover={onCellClick ? { opacity: 0.7 } : undefined}
                onClick={onCellClick ? () => onCellClick(undefined, row.month, row.rowTotal) : undefined}
              >
                {formatSigned(row.rowTotal)}
              </Td>
            </Tr>
          ))}
        </Tbody>

        <Tfoot>
          <Tr bg={tfootBg}>
            <Th fontWeight="black">Total</Th>
            {columns.map(col => {
              const val = colTotals[col] || 0;
              return (
                <Th
                  key={col}
                  isNumeric
                  fontWeight="black"
                  color={val < 0 ? 'red.500' : val > 0 ? 'green.600' : 'inherit'}
                  cursor={onCellClick ? 'pointer' : undefined}
                  _hover={onCellClick ? { opacity: 0.7 } : undefined}
                  onClick={onCellClick ? () => onCellClick(col, undefined, val) : undefined}
                >
                  {formatSigned(val)}
                </Th>
              );
            })}
            {hasChecks && (
              <>
                <Th isNumeric fontWeight="black" color={checkData.totals.in > 0 ? 'green.600' : 'inherit'}>
                  {formatSigned(checkData.totals.in)}
                </Th>
                <Th isNumeric fontWeight="black" color={checkData.totals.out > 0 ? 'red.500' : 'inherit'}>
                  {formatSigned(-checkData.totals.out)}
                </Th>
              </>
            )}
            {hasZelle && (
              <>
                <Th isNumeric fontWeight="black" color={zelleData.totals.in > 0 ? 'green.600' : 'inherit'}>
                  {formatSigned(zelleData.totals.in)}
                </Th>
                <Th isNumeric fontWeight="black" color={zelleData.totals.out > 0 ? 'red.500' : 'inherit'}>
                  {formatSigned(-zelleData.totals.out)}
                </Th>
              </>
            )}
            <Th
              isNumeric
              fontWeight="black"
              fontSize="md"
              color={
                colTotals.grandTotal < 0
                  ? 'red.500'
                  : colTotals.grandTotal > 0
                  ? 'green.600'
                  : 'inherit'
              }
            >
              {formatSigned(colTotals.grandTotal)}
            </Th>
          </Tr>
        </Tfoot>
      </Table>
    </Box>
    </Box>

    <AiAnalysisPanel
      isOpen={isPanelOpen}
      onClose={closePanel}
      matrixData={xactoData}
      groupBy={groupBy}
    />
    </>
  );
};
